import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { DailyNavExpectedAllocation } from 'api';
import { ApiError } from 'api/core/ApiError';
import { DailyNavNoteAttachmentUpload } from 'api/models/DailyNavNoteAttachmentUpload';
import { FirmsService } from 'api/services/FirmsService';
import { DAILY_NAV_RECORDS_BY_FIRM_QUERY_KEY } from 'common/constants/services/firms';
import {
  getBaseFileName,
  getSupportedFileType,
  getUploadKeyParts,
  uploadFileToSignedUrl,
} from 'services/hooks/documents/useFirmDocumentActions';
import { DAILY_NAV_REPORT_QUERY_KEY, DAILY_NAV_VALIDATE_QUERY_KEY } from '../useDailyNavReport';

// The note-attachment upload, at the day level: presign under the firm, PUT,
// and hand the server back what it needs to file each one.
const uploadDailyNavFinalizeFiles = async (
  firmId: number | undefined,
  files: File[]
): Promise<DailyNavNoteAttachmentUpload[]> =>
  Promise.all(
    files.map(async file => {
      const fileType = getSupportedFileType(file);
      const { url, key } = await FirmsService.apiFirmsDailyNavFinalizeAttachmentsUploadUrlList(
        String(firmId),
        fileType
      );
      await uploadFileToSignedUrl({ file, url });
      const { filePath, guid } = getUploadKeyParts(key);
      return { file_path: filePath, guid, file_type: fileType, filename: getBaseFileName(file, fileType) };
    })
  );

type FinalizeDailyNavDayVariables = {
  date?: string;
  finalize_approver_note?: string;
  staff_finalize_confirmation?: string;
  // Attached to the approver note. Presigned and PUT here, then filed by the
  // server under Daily NAV / <this report date> / final report.
  files?: File[];
  // From the validate response, echoed back unchanged. The server refuses if
  // its own resolution differs, so the day commits what was reviewed.
  expected_allocations: DailyNavExpectedAllocation[];
};

// The server's refusal reason ({detail}). The generated client rejects with the
// raw Axios error, not ApiError, so read both shapes before falling back.
export const getFinalizeErrorMessage = (error: unknown): string => {
  const body =
    error instanceof ApiError ? error.body : (error as { response?: { data?: unknown } } | null)?.response?.data;
  const detail = typeof body === 'object' && body ? (body as Record<string, unknown>).detail : undefined;
  if (typeof detail === 'string' && detail) {
    return detail;
  }
  return 'Failed to finalize Daily NAV.';
};

const useFinalizeDailyNavDay = (firmId: number | undefined) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({
      date,
      finalize_approver_note,
      staff_finalize_confirmation,
      files = [],
      expected_allocations,
    }: FinalizeDailyNavDayVariables) =>
      FirmsService.apiFirmsDailyNavFinalizeCreate(
        String(firmId),
        {
          finalize_approver_note,
          staff_finalize_confirmation,
          expected_allocations,
          finalize_attachments: await uploadDailyNavFinalizeFiles(firmId, files),
        },
        date
      ),
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: [DAILY_NAV_RECORDS_BY_FIRM_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [DAILY_NAV_VALIDATE_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [DAILY_NAV_REPORT_QUERY_KEY] });
      enqueueSnackbar(`Finalized ${data.finalized_count} Daily NAV record(s) for ${data.date}.`, {
        variant: 'success',
      });
      if (data.unlinked_companies?.length) {
        enqueueSnackbar(`No finalized valuation to link: ${data.unlinked_companies.join(', ')}.`, {
          variant: 'warning',
        });
      }
    },
    onError: (error: unknown) => {
      enqueueSnackbar(getFinalizeErrorMessage(error), { variant: 'error' });
    },
  });
};

export default useFinalizeDailyNavDay;
